import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Disclaimer } from "@/components/common";
import { BasicsSection, FoodSection, GoalsSection, ModulesSection, toPayload } from "@/components/profile/ProfileSections";
import { AilmentForm, emptyAilment, ZONE_LABELS } from "@/components/AilmentForm";

const STEPS = [
  { key: "basics", title: "Cuéntanos sobre ti", sub: "Con esto calculamos tus calorías y tu plan." },
  { key: "goals", title: "Objetivos y entrenamiento", sub: "Así tu entrenador sabe por dónde empezar." },
  { key: "food", title: "Tu alimentación", sub: "Respetaremos tus alergias y preferencias." },
  { key: "ailments", title: "Dolencias o limitaciones", sub: "Opcional. Adaptamos los ejercicios para cuidarte." },
  { key: "modules", title: "Elige tus pilares", sub: "Activa lo que quieras. Puedes cambiarlo cuando quieras." },
];

const validate = (key, f) => {
  if (key === "basics" && (!f.name || !f.birth_date || !f.height_cm || !f.weight_kg)) return "Completa nombre, fecha de nacimiento, altura y peso.";
  if (key === "goals" && !f.goals.length) return "Elige al menos un objetivo.";
  if (key === "modules" && !Object.values(f.modules).some(Boolean)) return "Activa al menos un módulo.";
  return "";
};

const AilmentsStep = ({ list, setList }) => {
  const [draft, setDraft] = useState(emptyAilment());
  const add = () => {
    if (!draft.name.trim()) return toast.error("Describe brevemente la dolencia.");
    setList([...list, draft]);
    setDraft(emptyAilment());
  };
  return (
    <div className="space-y-6">
      <Disclaimer />
      {list.map((a, i) => (
        <div key={i} className="flex items-center justify-between rounded-2xl bg-care-soft p-4" data-testid={`onboarding-ailment-${i}`}>
          <div><p className="font-semibold text-care-ink">{a.name}</p><p className="text-xs text-stone-600">{ZONE_LABELS[a.zone]} · dolor {a.intensity}/10</p></div>
          <button onClick={() => setList(list.filter((_, j) => j !== i))} data-testid={`onboarding-ailment-remove-${i}`}><Trash2 className="h-4 w-4 text-stone-500" /></button>
        </div>
      ))}
      <AilmentForm a={draft} set={(p) => setDraft({ ...draft, ...p })} />
      <button type="button" onClick={add} data-testid="onboarding-add-ailment-btn" className="flex items-center gap-2 rounded-full border-2 border-care px-5 py-2.5 text-sm font-semibold text-care-ink transition-transform active:scale-95">
        <Plus className="h-4 w-4" /> Añadir dolencia
      </button>
      <p className="text-xs text-stone-500">Si no tienes ninguna, pulsa «Siguiente».</p>
    </div>
  );
};

export default function Onboarding() {
  const { me, refreshMe } = useAuth();
  const nav = useNavigate();
  const [options, setOptions] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ailments, setAilments] = useState([]);
  const [f, setF] = useState({ name: me.name, birth_date: "", gender: "femenino", height_cm: "", weight_kg: "", activity_level: "ligero", goals: [], specific_goals: [], experience: "Principiante", training_days_per_week: 3, has_gym: false, sports_preferences: [], diet_type: "omnivora", allergies: [], dislikes: "", supplements: [], medical_notes: "", modules: { training: true, nutrition: true, ailments: false } });
  const set = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => { api.get("/options").then((r) => setOptions(r.data)); }, []);
  useEffect(() => { if (ailments.length) set({ modules: { ...f.modules, ailments: true } }); }, [ailments.length]); // eslint-disable-line

  if (!options) return <Loader />;
  const cur = STEPS[step];

  const next = async () => {
    const err = validate(cur.key, f);
    if (err) return toast.error(err);
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try {
      await api.put("/profile", toPayload(f));
      for (const a of ailments) await api.post("/ailments", a);
      await refreshMe();
      toast.success("¡Tu plan está listo!");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-screen max-w-2xl px-5 pb-32 pt-8 sm:pt-14">
      <div className="flex gap-1.5" data-testid="onboarding-progress">
        {STEPS.map((s, i) => <span key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-ink" : "bg-stone-200"}`} />)}
      </div>
      {me.role === "admin" && <a href="/admin" data-testid="onboarding-admin-link" className="mt-4 inline-block text-xs font-semibold text-stone-500 underline">Ir a Conectores (admin)</a>}
      <p className="eyebrow mt-8 text-train-ink">Paso {step + 1} de {STEPS.length}</p>
      <AnimatePresence mode="wait">
        <motion.div key={cur.key} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
          <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-testid="onboarding-step-title">{cur.title}</h1>
          <p className="mb-8 mt-2 text-stone-600">{cur.sub}</p>
          {cur.key === "basics" && <BasicsSection f={f} set={set} />}
          {cur.key === "goals" && <GoalsSection f={f} set={set} options={options} />}
          {cur.key === "food" && <FoodSection f={f} set={set} options={options} />}
          {cur.key === "ailments" && <AilmentsStep list={ailments} setList={setAilments} />}
          {cur.key === "modules" && <ModulesSection modules={f.modules} onChange={(m) => set({ modules: m })} />}
        </motion.div>
      </AnimatePresence>
      <div className="fixed inset-x-0 bottom-0 border-t border-black/5 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl gap-3 px-5 py-4">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn" className="flex items-center gap-2 rounded-full border border-stone-300 px-5 py-3 font-semibold transition-transform active:scale-95">
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
          )}
          <button disabled={busy} onClick={next} data-testid="onboarding-next-btn" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-ink py-3 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60">
            {busy ? "Creando tu plan…" : step === STEPS.length - 1 ? "Crear mi plan" : "Siguiente"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
